import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router";
import { DataTable } from "primereact/datatable";
import type { DataTableValueArray } from "primereact/datatable";
import { Column } from "primereact/column";
import { Card } from "primereact/card";

import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { useToast } from "sj-ui-toolkit/src/hooks";
import { useUserContext } from "sj-ui-toolkit/src/atlas/contexts";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";
import {
  DEFAULT_ROW_COUNT,
  ROWS_PER_PAGE_OPTIONS,
} from "sj-ui-toolkit/src/constants/tableRowsCounts";
import { mapRankToLevel } from "sj-ui-toolkit/src/utils/general";
import { getIsImpersonating, impersonateAsync } from "sj-ui-toolkit/src/utils/user";

import type { UserProgressData, UserProfileStatsData, LessonActivity } from "@/api/progressReport";
import { loadUserProgress, loadUserProfileStats } from "@/api/progressReport";
import { UserProfileHeader } from "@/components/UserProfileHeader";
import { withSkeleton, skeletonRows, dateBody } from "@/helpers/tableBody";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PageState {
  progressData: UserProgressData | null;
  profileData: UserProfileStatsData | null;
  progressLoading: boolean;
  profileLoading: boolean;
}

// ---------------------------------------------------------------------------
// Column body factories
// ---------------------------------------------------------------------------

const lessonNameBody = withSkeleton<LessonActivity>(row => row.name ?? "--", "20rem");
const completedAtBody = dateBody<LessonActivity>("completedAt", "N/A");
const resultBody = withSkeleton<LessonActivity>(
  row => (row.hackedu ? "N/A" : (row.result ?? "N/A")),
  "5rem"
);
const passedBody = withSkeleton<LessonActivity>(row => {
  if (row.passed == null) return "N/A";
  return <i className={`pi ${row.passed ? "pi-check-circle" : "pi-times-circle text-warning"}`} />;
}, "4rem");

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function ProgressReportUser() {
  const { user_id } = useParams<{ user_id: string }>();
  const { showToast } = useToast();
  const { isSjAdmin } = useUserContext();
  const impersonating = getIsImpersonating();

  const [state, setState] = useState<PageState>({
    progressData: null,
    profileData: null,
    progressLoading: true,
    profileLoading: true,
  });

  const userEmail = state.progressData?.roleEnrollment?.email;

  useBreadcrumb(
    userEmail
      ? {
          label: userEmail,
          route: `${AdminAppRoutes.routes.reporting.routes.progress.route}/${user_id ?? ""}`,
          display: "page",
        }
      : null
  );

  useEffect(() => {
    if (!user_id) return;

    setState({
      progressData: null,
      profileData: null,
      progressLoading: true,
      profileLoading: true,
    });

    const loadData = async () => {
      let progressData: UserProgressData;
      try {
        progressData = await loadUserProgress(user_id);
      } catch (error) {
        console.error("Failed to load user progress:", error);
        showToast({ severity: "error", detail: "Failed to load user progress data" });
        setState(prev => ({ ...prev, progressLoading: false, profileLoading: false }));
        return;
      }

      let profileData: UserProfileStatsData | null = null;
      const profileUserId = progressData?.roleEnrollment?.userId;
      if (profileUserId) {
        try {
          profileData = await loadUserProfileStats(profileUserId);
        } catch (error) {
          console.error("Failed to load user profile stats:", error);
        }
      }

      setState({
        progressData,
        profileData,
        progressLoading: false,
        profileLoading: false,
      });
    };

    void loadData();
  }, [user_id]);

  const handleImpersonateClick = useCallback(async () => {
    const email = state.progressData?.roleEnrollment?.email;
    if (!email) return;
    await impersonateAsync(email);
  }, [state.progressData?.roleEnrollment?.email]);

  const { progressData, profileData, progressLoading, profileLoading } = state;
  const { roleEnrollment, lessons } = progressData ?? {};
  const { totalTime, lastMonthTotalTime, lastMonthPoints, totalPoints, currentLevel } =
    profileData ?? {};

  const user = {
    name: {
      givenName: roleEnrollment?.firstName,
      familyName: roleEnrollment?.lastName,
    },
    email: roleEnrollment?.email,
  };

  const level = currentLevel?.levelName ? mapRankToLevel(currentLevel.levelName) : undefined;

  const stats = {
    totalPoints: totalPoints ?? null,
    lastMonthPoints: lastMonthPoints ?? null,
    totalTime: totalTime ?? null,
    lastMonthTime: lastMonthTotalTime ?? null,
  };

  const tableData = progressLoading
    ? skeletonRows<LessonActivity>(DEFAULT_ROW_COUNT)
    : (lessons ?? []);

  return (
    <div className="root">
      <UserProfileHeader
        user={user}
        impersonating={impersonating}
        trialMode={false}
        isSjAdmin={isSjAdmin}
        handleImpersonateClick={() => void handleImpersonateClick()}
        isLoading={profileLoading}
        stats={stats}
        level={level}
      />

      <div>
        <h2 className="atlas-h3 mb-4">Lesson Activity History</h2>
        <Card>
          <DataTable
            value={tableData as unknown as DataTableValueArray}
            paginator
            aria-label="User progress details"
            rows={DEFAULT_ROW_COUNT}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            emptyMessage="No lesson history available."
            tableClassName="dt-responsive-text"
          >
            <Column
              sortable
              field="name"
              header="Lesson Name"
              style={{ width: "500px" }}
              body={lessonNameBody}
            />
            <Column sortable field="completedAt" header="Date Completed" body={completedAtBody} />
            <Column sortable field="result" header="Last Result" body={resultBody} />
            <Column sortable field="passed" header="Passed" body={passedBody} />
          </DataTable>
        </Card>
      </div>
    </div>
  );
}
